import { useEffect, useState } from "react";
import styled from "styled-components";
import Countdown from "react-countdown";
import { Button, CircularProgress, Snackbar } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import logo from "./images/logo.png";
import con from "./images/connect.png";
import mintImg from "./images/mint.png";
import soldOut from "./images/soldOut.png";

import * as anchor from "@project-serum/anchor";

import { LAMPORTS_PER_SOL } from "@solana/web3.js";

import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { WalletDialogButton } from "@solana/wallet-adapter-material-ui";

import {
  CandyMachine,
  awaitTransactionSignatureConfirmation,
  getCandyMachineState,
  mintOneToken,
  shortenAddress,
} from "./candy-machine";

const ConnectButton = styled(WalletDialogButton)`
  && {
    width: 350px;
    /* height: 70px; */
    font-family: "Bloks";
    font-size: 38px;
    letter-spacing: 2.5px;
    font-weight: 500;
    color: #440000;
    background-color: #cdf7855c;
    border: 5px solid #61901d;
    border-radius: 20px;
    box-shadow: -3px 5px 1px #7c9b4887;
    transition: all 0.3s ease 0s;
    cursor: pointer;
    outline: none;
    justify-content: space-around;

    &:hover {
      background-color: #cdf7855c;
      border-radius: 50px;
      box-shadow: 0px 2px 1px #7c9b4887;
      transform: translateY(7px);
    }

  }
`;

const CounterText = styled.span`
  &&{
    font-family: "RainyHearts";
    font-size:40px;
    -webkit-text-stroke: 1px #440000;
    -webkit-text-fill-color: #f7ed85;
  } 
`; // add your styles here

const MintContainer = styled.div``; // add your styles here

const MintButton = styled(Button)`
&& {
  width: 350px;
  /* height: 70px; */
  font-family: "Bloks";
  font-size: 38px;
  letter-spacing: 2.5px;
  font-weight: 500;
  background-color: #cdf7855c;
  border: 5px solid #61901d;
  border-radius: 20px;
  box-shadow: -3px 5px 1px #7c9b4887;
  transition: all 0.3s ease 0s;
  cursor: pointer;
  outline: none;
  justify-content: space-around;

  &:hover {
    background-color: #cdf7855c;
    border-radius: 50px;
    box-shadow: 0px 2px 1px #7c9b4887;
    transform: translateY(7px);
  }
}
`; // add your styles here

export interface HomeProps {
  candyMachineId: anchor.web3.PublicKey;
  config: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  startDate: number;
  treasury: anchor.web3.PublicKey;
  txTimeout: number;
}


const Home = (props: HomeProps) => {
  const [balance, setBalance] = useState<number>();
  const [isActive, setIsActive] = useState(false); // true when countdown completes
  const [isSoldOut, setIsSoldOut] = useState(false); // true when items remaining is zero
  const [isMinting, setIsMinting] = useState(false); // true when user got to press MINT

  const [itemsAvailable, setItemsAvailable] = useState(0);
  const [itemsRedeemed, setItemsRedeemed] = useState(0);
  const [itemsRemaining, setItemsRemaining] = useState(0);

  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: "",
    severity: undefined,
  });
  const [startDate, setStartDate] = useState(new Date(props.startDate));

  const wallet = useAnchorWallet();
  const [candyMachine, setCandyMachine] = useState<CandyMachine>();

  const refreshCandyMachineState = () => {
    (async () => {
      if (!wallet) return;

      const {
        candyMachine,
        goLiveDate,
        itemsAvailable,
        itemsRemaining,
        itemsRedeemed,
      } = await getCandyMachineState(
        wallet as anchor.Wallet,
        props.candyMachineId,
        props.connection
      );

      setItemsAvailable(itemsAvailable);
      setItemsRemaining(itemsRemaining);
      setItemsRedeemed(itemsRedeemed);
      setIsActive(false);
      setIsSoldOut(itemsRemaining === 0);
      setStartDate(goLiveDate);
      setCandyMachine(candyMachine);
    })();
  };

  const onMint = async () => {
    try {
      setIsMinting(true);
      if (wallet && candyMachine?.program) {
        const mintTxId = await mintOneToken(
          candyMachine,
          props.config,
          wallet.publicKey,
          props.treasury
        );

        const status = await awaitTransactionSignatureConfirmation(
          mintTxId,
          props.txTimeout,
          props.connection,
          "singleGossip",
          false
        );

        if (!status?.err) {
          setAlertState({
            open: true,
            message: "Congratulations! Mint succeeded!",
            severity: "success",
          });
        } else {
          setAlertState({
            open: true,
            message: "Mint failed! Please try again!",
            severity: "error",
          });
        }
      }
    } catch (error: any) {
      // TODO: blech:
      let message = error.msg || "Minting failed! Please try again!";
      if (!error.msg) {
        if (error.message.indexOf("0x138")) {
        } else if (error.message.indexOf("0x137")) {
          message = `SOLD OUT!`;
        } else if (error.message.indexOf("0x135")) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          message = `SOLD OUT!`;
          setIsSoldOut(true);
        } else if (error.code === 312) {
          message = `Minting period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      if (wallet) {
        const balance = await props.connection.getBalance(wallet.publicKey);
        setBalance(balance / LAMPORTS_PER_SOL);
      }
      setIsMinting(false);
      refreshCandyMachineState();
    }
  };

  useEffect(() => {
    (async () => {
      if (wallet) {
        const balance = await props.connection.getBalance(wallet.publicKey);
        setBalance(balance / LAMPORTS_PER_SOL);
      }
    })();
  }, [wallet, props.connection]);

  useEffect(refreshCandyMachineState, [
    wallet,
    props.candyMachineId,
    props.connection,
  ]);

  return (
    <main>
      <div className="grids">
        <div className="logo">
          {/* <h1 className="Bloks">The Vegetarians</h1> */}
          <img src={logo} alt="" />
        </div>
        <div className="text">
          <p className="Description">The Vegetarians are a group of humanoid vegetables from a parallel universe. They  raise their young in their sacred garden called Edenana and are appalled that in our universe we Eat Our Veggies!</p> <p> Mint your own Vegetarian and become a seed investor in the Multiverse.</p>
        </div>
        <div className="available">
          {!wallet ? (<h1 className="Connection">Connect Wallet</h1>) : (
            <div className="info">
              <p className="BloksNormal">Minted: {itemsRedeemed}</p>
              <p className="BloksNormal">Available: {itemsRemaining}</p>
            </div>)}
        </div>
        <div className="mint">
          <MintContainer>
            {!wallet ? (
              isActive ? (<ConnectButton><img src={con} style={{ width: "50%" }}alt="" /></ConnectButton>) : (
                <Countdown
                  date={startDate}
                  onMount={({ completed }) => completed && setIsActive(true)}
                  onComplete={() => setIsActive(true)}
                  renderer={renderCounter}
                />
              )
            ) : (
              <MintButton disabled={isSoldOut || isMinting || !isActive} onClick={onMint} variant="contained">
                {isSoldOut ? (<img src={soldOut} style={{ width: "70%" }}alt="" />) : isActive ? (isMinting ? (<CircularProgress />) : (<img src={mintImg} style={{ width: "40%" }}alt="" />)) : (
                  <Countdown
                    date={startDate}
                    onMount={({ completed }) => completed && setIsActive(true)}
                    onComplete={() => setIsActive(true)}
                    renderer={renderCounter}
                  />
                )}
              </MintButton>
            )}
          </MintContainer>
        </div>
      </div>

      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert
          onClose={() => setAlertState({ ...alertState, open: false })}
          severity={alertState.severity}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

interface AlertState {
  open: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error" | undefined;
}

const renderCounter = ({ days, hours, minutes, seconds, completed }: any) => {
  return (
    <CounterText>
      {hours + (days || 0) * 24} H, {minutes} M, {seconds} S
    </CounterText>
  );
};

export default Home;
